import React, { Suspense, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Button, Container, Link, Paper, Stack, Switch, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useCookieConsent } from './useCookieConsent';

const ACTION_BUTTON_SX = { flex: 1, minWidth: 0, textTransform: 'none' as const };

const BannerContent: React.FC = () => {
  const { t } = useTranslation('legal');
  const { consent, needsDecision, settingsRequested, acceptAll, rejectNonEssential, save, closeSettings } =
    useCookieConsent();
  const [configuring, setConfiguring] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  const visible = needsDecision || settingsRequested;

  // Reopened from the footer: go straight to the settings, starting from the stored choice.
  useEffect(() => {
    if (settingsRequested) {
      setConfiguring(true);
      setAnalytics(consent?.categories.analytics ?? false);
      setMarketing(consent?.categories.marketing ?? false);
    }
  }, [settingsRequested, consent]);

  useEffect(() => {
    if (!visible) setConfiguring(false);
  }, [visible]);

  if (!visible) return null;

  const categories = [
    { key: 'essential', checked: true, disabled: true, onChange: undefined },
    { key: 'analytics', checked: analytics, disabled: false, onChange: setAnalytics },
    { key: 'marketing', checked: marketing, disabled: false, onChange: setMarketing },
  ] as const;

  return (
    <Paper
      role="dialog"
      aria-modal="false"
      aria-labelledby="cookie-consent-title"
      elevation={0}
      sx={{ position: 'fixed', borderTop: 1, borderColor: 'divider', left: 0, right: 0, bottom: 0, zIndex: (theme) => theme.zIndex.modal + 1, borderRadius: 0, py: 2 }}
    >
      <Container maxWidth="md">
        <Typography id="cookie-consent-title" variant="h6" component="h2" fontWeight={700} gutterBottom>
          {configuring ? t('banner.configureTitle') : t('banner.title')}
        </Typography>

        {configuring ? (
          <Stack spacing={1.5} sx={{ mb: 2 }}>
            {categories.map((category) => (
              <Box key={category.key} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                <Box>
                  <Typography component="label" htmlFor={`cookie-${category.key}`} fontWeight={600}>
                    {t(`banner.categories.${category.key}.name`)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t(`banner.categories.${category.key}.description`)}
                  </Typography>
                </Box>
                <Switch
                  id={`cookie-${category.key}`}
                  checked={category.checked}
                  disabled={category.disabled}
                  onChange={category.onChange ? (e) => category.onChange(e.target.checked) : undefined}
                />
              </Box>
            ))}
          </Stack>
        ) : (
          <Typography variant="body2" sx={{ mb: 2 }}>
            {t('banner.textLead')}{' '}
            <Link component={RouterLink} to="/cookies">
              {t('banner.cookiePolicy')}
            </Link>{' '}
            {t('banner.textAnd')}{' '}
            <Link component={RouterLink} to="/privacy-policy">
              {t('banner.privacyPolicy')}
            </Link>
            .
          </Typography>
        )}

        {configuring ? (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <Button
              variant="outlined"
              sx={ACTION_BUTTON_SX}
              onClick={() => (settingsRequested ? closeSettings() : setConfiguring(false))}
            >
              {t('banner.back')}
            </Button>
            <Button variant="outlined" sx={ACTION_BUTTON_SX} onClick={() => save({ analytics, marketing })}>
              {t('banner.save')}
            </Button>
          </Stack>
        ) : (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <Button variant="outlined" sx={ACTION_BUTTON_SX} onClick={acceptAll}>
              {t('banner.acceptAll')}
            </Button>
            <Button variant="outlined" sx={ACTION_BUTTON_SX} onClick={rejectNonEssential}>
              {t('banner.rejectNonEssential')}
            </Button>
            <Button variant="outlined" sx={ACTION_BUTTON_SX} onClick={() => setConfiguring(true)}>
              {t('banner.configure')}
            </Button>
          </Stack>
        )}
      </Container>
    </Paper>
  );
};

/** Cookie consent banner; mount once in the app shell. Its translations load lazily, hence the Suspense. */
export const CookieConsentBanner: React.FC = () => (
  <Suspense fallback={null}>
    <BannerContent />
  </Suspense>
);

export default CookieConsentBanner;
